<template>
  <div class="legal-consent">
    <APageHead
      title="약관 동의 증빙 조회"
      sub="누가 언제 어떤 버전에 동의했는지 조회합니다. (분쟁·감사 대응)"
      back
      @back="goDocuments"
    />

    <!-- 검색 -->
    <ACard overflow="visible">
      <div class="legal-consent__filters">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">alternate_email</i>
            회원 이메일
          </label>
          <input
            v-model.trim="filters.userEmail"
            class="p-input"
            type="text"
            placeholder="이메일 일부 입력"
            @keyup.enter="search"
          />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">gavel</i>
            문서
          </label>
          <select v-model="filters.documentId" class="p-input">
            <option value="">전체 문서</option>
            <option v-for="d in documents" :key="d.documentId" :value="d.documentId">
              {{ (d.names && d.names.ko) || d.documentId }}
            </option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">rule</i>
            동의 여부
          </label>
          <select v-model="filters.agreed" class="p-input">
            <option value="">전체</option>
            <option value="true">동의</option>
            <option value="false">철회·거부</option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">event</i>
            시작일
          </label>
          <input v-model="filters.dateFrom" class="p-input" type="date" />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">event_available</i>
            종료일
          </label>
          <input v-model="filters.dateTo" class="p-input" type="date" />
        </div>
        <div class="legal-consent__filter-actions">
          <ABtn kind="primary" icon="search" @click="search">조회</ABtn>
          <ABtn kind="ghost" icon="refresh" @click="resetFilters">초기화</ABtn>
        </div>
      </div>
    </ACard>

    <!-- 결과 -->
    <ACard>
      <template #title>
        <span class="legal-consent__sec-title">
          <i class="material-icons-round legal-consent__sec-icon">fact_check</i>
          동의 기록
        </span>
      </template>
      <template #action>
        <APill kind="info">{{ total }}건</APill>
      </template>

      <div v-if="loading" class="legal-consent__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>동의 기록을 불러오는 중...</p>
      </div>

      <AEmpty
        v-else-if="!items.length"
        title="조회 결과가 없습니다"
        sub="검색 조건을 바꾸거나 기간을 넓혀 보세요."
      />

      <template v-else>
        <ATable :columns="columns" :rows="items" striped bordered>
          <template #cell:user="{ row }">
            <button type="button" class="legal-consent__user" @click="openUser(row)">
              <span class="legal-consent__user-email">{{ row.userEmail || '(익명화됨)' }}</span>
              <span class="legal-consent__user-idx">#{{ row.userIdx }}</span>
            </button>
          </template>

          <template #cell:documentId="{ row }">
            <span class="legal-consent__mono">{{ row.documentId }}</span>
          </template>

          <!-- 사용자가 실제로 본 버전. 클릭하면 그 본문을 그대로 재구성해 볼 수 있습니다. -->
          <template #cell:version="{ row }">
            <ABtn size="sm" kind="ghost" @click="openVersion(row)">v{{ row.version }}</ABtn>
          </template>

          <template #cell:agreed="{ row }">
            <APill :kind="row.agreed ? 'ok' : 'err'" dot>{{ row.agreed ? '동의' : '철회' }}</APill>
          </template>

          <template #cell:source="{ row }">
            <APill kind="default">{{ consentSourceLabel(row.source) }}</APill>
          </template>

          <template #cell:agreedAt="{ row }">
            <span class="legal-consent__date">{{ fmtKst(row.agreedAt) }}</span>
          </template>

          <template #cell:ipAddress="{ row }">
            <span class="legal-consent__ip" :title="row.userAgent">{{ row.ipAddress || '-' }}</span>
          </template>

          <template #cell:_flags="{ row }">
            <APill v-if="row.anonymizedAt" kind="dark">탈퇴(익명화)</APill>
            <span v-else class="legal-consent__dash">—</span>
          </template>
        </ATable>

        <APagination
          :current-page="page"
          :total="total"
          :page-size="size"
          @change="onPage"
        />
      </template>
    </ACard>

    <LegalVersionViewerModal
      v-model="showViewer"
      :document-id="viewerDocumentId"
      :version="viewerVersion"
    />

    <LegalUserConsentModal
      v-model="showUser"
      :user-idx="userTarget.userIdx || ''"
      :user-email="userTarget.userEmail || ''"
    />
  </div>
</template>

<script>
import swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import LegalVersionViewerModal from "./components/LegalVersionViewerModal.vue";
import LegalUserConsentModal from "./components/LegalUserConsentModal.vue";
import { apiErrorMessage } from "@/utils/apiError";
import { fmtKst, consentSourceLabel } from "@/utils/legal";

const blankFilters = () => ({
  userEmail: "",
  documentId: "",
  agreed: "",
  dateFrom: "",
  dateTo: "",
});

export default {
  name: "SystemLegalConsents",
  components: {
    APageHead,
    ACard,
    ABtn,
    APill,
    ATable,
    AEmpty,
    APagination,
    LegalVersionViewerModal,
    LegalUserConsentModal,
  },
  data() {
    return {
      loading: true,
      documents: [],
      items: [],
      total: 0,
      page: 1,
      size: 50,
      filters: blankFilters(),
      showViewer: false,
      viewerDocumentId: "",
      viewerVersion: "",
      showUser: false,
      userTarget: {},
      columns: [
        { key: "user", label: "회원", width: 220 },
        { key: "documentId", label: "문서", width: 175 },
        { key: "version", label: "버전", width: 85, align: "center" },
        { key: "agreed", label: "상태", width: 85, align: "center" },
        { key: "source", label: "경로", width: 115, align: "center" },
        { key: "method", label: "방식", width: 90, align: "center" },
        { key: "agreedAt", label: "동의 시각 (KST)", width: 145 },
        { key: "ipAddress", label: "IP", width: 130 },
        { key: "_flags", label: "비고", width: 110, align: "center" },
      ],
    };
  },
  created() {
    this.fetchDocuments();
    this.fetchConsents();
  },
  methods: {
    fmtKst,
    consentSourceLabel,
    goDocuments() {
      this.$router.push({ name: "SystemLegalDocuments" });
    },
    openVersion(row) {
      this.viewerDocumentId = row.documentId;
      this.viewerVersion = String(row.version);
      this.showViewer = true;
    },
    openUser(row) {
      this.userTarget = row;
      this.showUser = true;
    },
    search() {
      this.page = 1;
      this.fetchConsents();
    },
    resetFilters() {
      this.filters = blankFilters();
      this.search();
    },
    onPage(p) {
      this.page = p;
      this.fetchConsents();
    },
    // 문서 select 옵션용. 실패해도 조회 자체는 되도록 조용히 넘어갑니다.
    async fetchDocuments() {
      try {
        const res = await this.$axios({ method: "get", url: "/emp/legal/documents" });
        this.documents = res.data || [];
      } catch (e) {
        console.error("문서 목록 조회 실패(필터용):", e);
      }
    },
    async fetchConsents() {
      this.loading = true;
      try {
        // 빈 필터는 키 자체를 빼서 서버가 전체 조회로 처리하게 합니다.
        const params = { page: this.page, size: this.size };
        Object.keys(this.filters).forEach((k) => {
          if (this.filters[k] !== "") params[k] = this.filters[k];
        });

        const res = await this.$axios({
          method: "get",
          url: "/emp/legal/consents",
          params,
          loadingBar: true,
        });
        this.items = res.data?.items || [];
        this.total = res.data?.total || 0;
      } catch (e) {
        console.error("동의 증빙 조회 실패:", e);
        swal.fire("오류", apiErrorMessage(e, "동의 기록을 불러오지 못했습니다."), "error");
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.legal-consent {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__filters {
    display: grid;
    grid-template-columns: 1.4fr 1.2fr 1fr 1fr 1fr auto;
    gap: 12px;
    align-items: end;

    @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 720px) { grid-template-columns: 1fr; }
  }

  &__filter-actions {
    display: flex;
    gap: 6px;
  }

  &__sec-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__sec-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 24px;
    color: $p-ink-mid;

    p { margin: 0; font-size: 12px; }
  }

  &__user {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    font-family: $p-font-body;

    &:hover .legal-consent__user-email { color: $p-point-deep; }
  }

  &__user-email {
    font-size: 12px;
    font-weight: 700;
    color: $p-ink;
    transition: color $p-dur $p-ease;
  }

  &__user-idx {
    font-family: $p-font-mono;
    font-size: 10px;
    color: $p-ink-soft;
  }

  &__mono {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink-mid;
  }

  &__date {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink-mid;
  }

  &__ip {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__dash {
    color: $p-ink-softer;
  }
}
</style>
